import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Pagination, Tooltip } from "antd";

import { TYPE_COLOR, TYPE_ICON, type DocFileType, type FileKBStatus } from "@/mocks/documentsData";
import { usePersonalDocumentUploadQueue } from "@/hooks/usePersonalDocumentUploadQueue";
import {
  getPersonalDocumentStatusPollingIds,
  mergePersonalDocumentStatuses,
  personalDocumentApi,
  type PersonalDocument,
} from "@/services/personalDocuments";
import { formatFileSize, formatPersonalDocumentUploadedAt } from "@/utils/formatters";
import {
  isPersonalDocumentFileAllowed,
  PERSONAL_DOCUMENT_ACCEPT,
  PERSONAL_DOCUMENT_ALLOWED_EXTENSIONS,
  PERSONAL_DOCUMENT_MAX_FILE_COUNT,
} from "@/utils/personalDocumentFiles";
import DeleteConfirmDialog from "@/pages/documents/components/DeleteConfirmDialog";

interface DisplayDocument {
  id: string;
  name: string;
  type: DocFileType;
  size: string;
  uploadedAt: string;
}

type SortKey = "uploadedAt" | "name" | "size";
type SortDir = "asc" | "desc";
const PAGE_SIZE = 20;

function KBBadge({ status, isZh }: { status?: FileKBStatus; isZh: boolean }) {
  if (!status || status.status === "pending") {
    return (
      <span
        className="inline-flex items-center gap-1 rounded-full text-xs px-2 py-0.5 font-medium whitespace-nowrap"
        style={{ backgroundColor: "rgba(107,114,128,0.08)", color: "#9ca3af" }}
      >
        <i className="ri-checkbox-blank-circle-line text-xs"></i>
        {isZh ? "未开始" : "Not started"}
      </span>
    );
  }

  if (status.status === "running" && typeof status.progress === "number" && status.progress < 100) {
    const progress = status.progress;
    return (
      <div className="flex flex-col gap-0.5 items-end">
        <div className="flex items-center gap-1.5">
          <i className="ri-loader-4-line text-xs animate-spin" style={{ color: "#3b82f6" }}></i>
          <span className="text-[11px] font-medium" style={{ color: "#3b82f6" }}>
            {progress}%
          </span>
        </div>
        <div className="w-24 h-1.5 rounded-full bg-gray-100 overflow-hidden">
          <div
            className="h-full rounded-full transition-all duration-300"
            style={{ width: `${progress}%`, backgroundColor: "#3b82f6" }}
          ></div>
        </div>
      </div>
    );
  }

  if (status.status === "running") {
    return (
      <span
        className="inline-flex items-center gap-1 rounded-full text-xs px-2 py-0.5 font-medium whitespace-nowrap shrink-0"
        style={{ backgroundColor: "rgba(59,130,246,0.08)", color: "#3b82f6" }}
      >
        <i className="ri-loader-4-line text-xs animate-spin"></i>
        {isZh ? "构建中" : "Running"}
      </span>
    );
  }

  const cfg: Record<
    string,
    { icon: string; labelZh: string; labelEn: string; bg: string; color: string }
  > = {
    succeeded: {
      icon: "ri-check-line",
      labelZh: "Succeeded",
      labelEn: "Succeeded",
      bg: "rgba(22,163,74,0.08)",
      color: "#16a34a",
    },
    queued: {
      icon: "ri-time-line",
      labelZh: "Queued",
      labelEn: "Queued",
      bg: "rgba(245,158,11,0.08)",
      color: "#f59e0b",
    },
    failed: {
      icon: "ri-error-warning-line",
      labelZh: "Failed",
      labelEn: "Failed",
      bg: "rgba(239,68,68,0.08)",
      color: "#ef4444",
    },
  };
  const c = cfg[status.status] ?? cfg.queued;
  const badge = (
    <span
      className="inline-flex items-center gap-1 rounded-full text-xs px-2 py-0.5 font-medium whitespace-nowrap shrink-0"
      style={{ backgroundColor: c.bg, color: c.color }}
    >
      <i className={`${c.icon} text-xs`}></i>
      {isZh ? c.labelZh : c.labelEn}
    </span>
  );
  return badge;
}

function Checkbox({
  checked,
  indeterminate,
  onChange,
}: {
  checked: boolean;
  indeterminate?: boolean;
  onChange: () => void;
}) {
  return (
    <div
      onClick={onChange}
      className={`w-4 h-4 flex items-center justify-center rounded border transition-colors cursor-pointer ${
        checked || indeterminate
          ? "border-[#830051] bg-[#830051]"
          : "border-gray-300 hover:border-gray-400"
      }`}
    >
      {indeterminate ? (
        <div className="w-2 h-0.5 bg-white rounded"></div>
      ) : checked ? (
        <i className="ri-check-line text-white text-xs"></i>
      ) : null}
    </div>
  );
}

function DragOverlay({ isZh }: { isZh: boolean }) {
  return (
    <div className="fixed inset-0 z-40 bg-black/20 flex items-center justify-center">
      <div
        className="bg-white rounded-2xl px-12 py-10 flex flex-col items-center gap-3"
        style={{ boxShadow: "0 8px 40px rgba(0,0,0,0.12)" }}
      >
        <div
          className="w-14 h-14 flex items-center justify-center rounded-xl"
          style={{ backgroundColor: "rgba(131,0,81,0.08)" }}
        >
          <i className="ri-upload-cloud-2-line text-2xl" style={{ color: "#830051" }}></i>
        </div>
        <p className="text-base font-semibold text-gray-900">
          {isZh ? "松开以上传文件" : "Drop to upload"}
        </p>
        <p className="text-xs text-gray-400">
          {isZh ? "文件将直接加入列表" : "Files will be added to the list"}
        </p>
      </div>
    </div>
  );
}

export default function PersonalSourcesTab({ isZh }: { isZh: boolean }) {
  const [documents, setDocuments] = useState<PersonalDocument[]>([]);
  const documentsRef = useRef<PersonalDocument[]>([]);
  const [docs, setDocs] = useState<DisplayDocument[]>([]);
  const [loadError, setLoadError] = useState("");

  const [dragOver, setDragOver] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const retryingIdsRef = useRef<Set<string>>(new Set());
  const [retryingIds, setRetryingIds] = useState<Set<string>>(new Set());

  const [search, setSearch] = useState("");
  const [filterKB, setFilterKB] = useState("all");
  const [sortKey, setSortKey] = useState<SortKey>("uploadedAt");
  const [sortDir, setSortDir] = useState<SortDir>("desc");
  const [page, setPage] = useState(1);
  const [filteredTotal, setFilteredTotal] = useState(0);
  const [fileCount, setFileCount] = useState(0);
  const [maxFileCount, setMaxFileCount] = useState(PERSONAL_DOCUMENT_MAX_FILE_COUNT);

  const mapDocument = useCallback((document: PersonalDocument): DisplayDocument => {
    return {
      id: document.id,
      name: document.fileName,
      type: (document.fileExtension || "txt") as DocFileType,
      size: formatFileSize(document.fileSize),
      uploadedAt: formatPersonalDocumentUploadedAt(document.createdAt),
    };
  }, []);

  const refreshDocuments = useCallback(async () => {
    try {
      const result = await personalDocumentApi.search({
        search: search.trim() || undefined,
        status: filterKB === "all" ? undefined : filterKB,
        sortBy: sortKey === "uploadedAt" ? "createdAt" : sortKey === "name" ? "fileName" : "size",
        sortDescending: sortDir === "desc",
        page,
        pageSize: PAGE_SIZE,
      });
      if (page > 1 && result.items.length === 0 && result.total > 0) {
        setPage(Math.ceil(result.total / PAGE_SIZE));
        return;
      }
      documentsRef.current = result.items;
      setDocuments(result.items);
      setDocs(result.items.map(mapDocument));
      setFilteredTotal(result.total);
      setFileCount(result.totalFileCount ?? result.total);
      setMaxFileCount(result.maxFileCount ?? PERSONAL_DOCUMENT_MAX_FILE_COUNT);
      setLoadError("");
    } catch (error: any) {
      setLoadError(String(error?.response?.data?.message ?? error?.message ?? "Failed to load"));
    }
  }, [filterKB, mapDocument, page, search, sortDir, sortKey]);

  const pollDocumentStatuses = useCallback(async () => {
    const ids = getPersonalDocumentStatusPollingIds(documentsRef.current);
    if (ids.length === 0) return;
    try {
      const statuses = await personalDocumentApi.statuses(ids);
      setDocuments((current) => {
        const next = mergePersonalDocumentStatuses(current, statuses);
        documentsRef.current = next;
        return next;
      });
    } catch (error) {
      console.error("Personal document status polling failed:", error);
    }
  }, []);

  const uploadQueue = usePersonalDocumentUploadQueue({
    concurrency: 3,
    onChanged: refreshDocuments,
  });

  const unattachedUploadFailure = useMemo(
    () =>
      uploadQueue.items
        .filter((item) => item.phase === "failed" && !item.documentListId)
        .map((item) => `${item.file.name}: ${item.error ?? (isZh ? "上传失败" : "Upload failed")}`)
        .join("; "),
    [isZh, uploadQueue.items],
  );
  const visibleError = unattachedUploadFailure
    ? isZh
      ? `上传失败：${unattachedUploadFailure}`
      : `Upload failed: ${unattachedUploadFailure}`
    : loadError;

  useEffect(() => {
    const timer = window.setTimeout(() => void refreshDocuments(), 200);
    return () => window.clearTimeout(timer);
  }, [refreshDocuments]);

  const hasNonTerminal = getPersonalDocumentStatusPollingIds(documents).length > 0;
  useEffect(() => {
    if (!hasNonTerminal) return;
    const timer = window.setInterval(() => void pollDocumentStatuses(), 5000);
    return () => window.clearInterval(timer);
  }, [hasNonTerminal, pollDocumentStatuses]);

  const kbStatuses = useMemo<FileKBStatus[]>(() => {
    const statuses = documents.map<FileKBStatus>((document) => ({
      fileId: document.id,
      status:
        document.ragJobStatus ??
        (document.uploadStatus === "Failed" || document.uploadStatus === "Expired"
          ? "failed"
          : document.uploadStatus === "Uploaded"
            ? "queued"
            : "pending"),
      errorMsg: document.ragJobMessage ?? undefined,
    }));
    uploadQueue.items.forEach((item) => {
      if (!item.documentListId) return;
      const index = statuses.findIndex((candidate) => candidate.fileId === item.documentListId);
      // complete 后由轻量轮询状态接管；不能让队列里的静态 queued 覆盖 PIKE running/succeeded。
      if (item.phase === "queued" && index >= 0) return;
      const status: FileKBStatus = {
        fileId: item.documentListId,
        status:
          item.phase === "failed" || item.phase === "cancelled"
            ? "failed"
            : item.phase === "queued"
              ? "queued"
              : item.phase === "waiting"
                ? "pending"
                : "running",
        progress:
          item.phase === "uploading" || item.phase === "finalizing" ? item.progress : undefined,
        errorMsg: item.error,
      };
      if (index >= 0) statuses[index] = status;
      else statuses.push(status);
    });
    return statuses;
  }, [documents, uploadQueue.items]);

  const kbMap = useMemo(() => {
    const map: Record<string, FileKBStatus> = {};
    kbStatuses.forEach((status) => {
      map[status.fileId] = status;
    });
    return map;
  }, [kbStatuses]);

  const displayed = docs;

  const allDisplayedSelected =
    displayed.length > 0 && displayed.every((doc) => selectedIds.has(doc.id));
  const someDisplayedSelected =
    displayed.some((doc) => selectedIds.has(doc.id)) && !allDisplayedSelected;

  const toggleSelectAll = () => {
    if (allDisplayedSelected) {
      setSelectedIds((prev) => {
        const next = new Set(prev);
        displayed.forEach((doc) => next.delete(doc.id));
        return next;
      });
    } else {
      setSelectedIds((prev) => {
        const next = new Set(prev);
        displayed.forEach((doc) => next.add(doc.id));
        return next;
      });
    }
  };

  const toggleSelectRow = (id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const handleDelete = async () => {
    try {
      const idsToDelete = Array.from(selectedIds);
      const results = await personalDocumentApi.deleteBatch(idsToDelete);
      const failed = results.filter((item) => !item.success);
      setSelectedIds(new Set(failed.map((item) => item.documentListId)));
      setShowDeleteDialog(false);
      if (failed.length > 0) {
        setLoadError(
          isZh
            ? `${failed.length} 个文件删除失败，已保留选中状态`
            : `${failed.length} file(s) could not be deleted and remain selected`,
        );
      }
      await refreshDocuments();
    } catch (error: any) {
      setLoadError(
        String(error?.response?.data?.message ?? error?.response?.data?.Message ?? error?.message),
      );
    }
  };

  const retryRag = async (documentListId: string) => {
    const result = await personalDocumentApi.retry(documentListId);
    const normalizedStatus = String(result.status ?? "").toLowerCase();
    const ragJobStatus = ["queued", "running", "succeeded", "failed"].includes(normalizedStatus)
      ? (normalizedStatus as PersonalDocument["ragJobStatus"])
      : null;

    setDocuments((current) => {
      const document = current.find((candidate) => candidate.id === documentListId);
      if (!document) return current;
      const next = mergePersonalDocumentStatuses(current, [
        {
          documentListId,
          uploadStatus: document.uploadStatus,
          ragJobId: result.ragJobId ?? document.ragJobId,
          ragJobStatus: ragJobStatus ?? document.ragJobStatus,
          ragJobMessage: ragJobStatus === "failed" || !result.success ? result.message : null,
        },
      ]);
      documentsRef.current = next;
      return next;
    });
    setLoadError("");
  };

  const retryFailedDocument = async (documentListId: string) => {
    if (retryingIdsRef.current.has(documentListId)) return;
    retryingIdsRef.current.add(documentListId);
    setRetryingIds(new Set(retryingIdsRef.current));
    try {
      const uploadItem = uploadQueue.items.find(
        (item) =>
          item.documentListId === documentListId &&
          (item.phase === "failed" || item.phase === "cancelled"),
      );
      if (uploadItem) {
        await uploadQueue.retry(uploadItem.clientId);
        return;
      }
      await retryRag(documentListId);
    } catch (error: any) {
      setLoadError(
        String(error?.response?.data?.message ?? error?.response?.data?.Message ?? error?.message),
      );
    } finally {
      retryingIdsRef.current.delete(documentListId);
      setRetryingIds(new Set(retryingIdsRef.current));
    }
  };

  const downloadDocument = async (documentListId: string) => {
    try {
      await personalDocumentApi.download(documentListId);
    } catch (error: any) {
      setLoadError(
        String(error?.response?.data?.message ?? error?.response?.data?.Message ?? error?.message),
      );
    }
  };

  const processFiles = (fileList: FileList | null) => {
    if (!fileList || fileList.length === 0) return;
    const files = Array.from(fileList);
    const supportedFiles = files.filter((file) => isPersonalDocumentFileAllowed(file.name));
    const rejectedFiles = files.filter((file) => !isPersonalDocumentFileAllowed(file.name));
    const remainingSlots = Math.max(0, maxFileCount - fileCount);
    const acceptedFiles = supportedFiles.slice(0, remainingSlots);
    const limitRejectedCount = supportedFiles.length - acceptedFiles.length;
    const errors: string[] = [];

    if (rejectedFiles.length > 0) {
      const fileNames = rejectedFiles.map((file) => file.name).join(", ");
      const allowedTypes = PERSONAL_DOCUMENT_ALLOWED_EXTENSIONS.join(", ");
      errors.push(
        isZh
          ? `不支持以下文件：${fileNames}。仅支持：${allowedTypes}`
          : `Unsupported files: ${fileNames}. Allowed types: ${allowedTypes}`,
      );
    }

    if (limitRejectedCount > 0) {
      errors.push(
        isZh
          ? `个人文档最多保留 ${maxFileCount} 个，${limitRejectedCount} 个文件未加入。请删除已有文件后再上传。`
          : `Maximum ${maxFileCount} files reached. ${limitRejectedCount} file(s) were not added. Delete files to upload more.`,
      );
    }

    setLoadError(errors.join(" "));

    if (acceptedFiles.length > 0) {
      void uploadQueue.upload(acceptedFiles.map((file) => ({ file })));
    }
  };

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOver(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.currentTarget === e.target) setDragOver(false);
  }, []);

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOver(false);
    processFiles(e.dataTransfer.files);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    processFiles(e.target.files);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const toggleSort = (key: SortKey) => {
    setPage(1);
    if (sortKey === key) {
      setSortDir((dir) => (dir === "asc" ? "desc" : "asc"));
    } else {
      setSortKey(key);
      setSortDir("desc");
    }
  };

  const hasActiveFilter = !!search || filterKB !== "all";
  const maxFileCountReached = fileCount >= maxFileCount;
  const uploadLimitTooltip = isZh
    ? `最多只能上传 ${maxFileCount} 个文件。请删除文件后再上传。`
    : `Maximum ${maxFileCount} files reached. Delete files to upload more.`;

  const resetFilters = () => {
    setPage(1);
    setSearch("");
    setFilterKB("all");
  };

  const SortIcon = ({ k }: { k: SortKey }) => {
    if (sortKey !== k) return <i className="ri-arrow-up-down-line text-gray-300 text-[10px]"></i>;
    return (
      <i
        className={`${sortDir === "asc" ? "ri-arrow-up-line" : "ri-arrow-down-line"} text-[10px]`}
        style={{ color: "#830051" }}
      ></i>
    );
  };

  const selectedCount = selectedIds.size;

  return (
    <div
      className="flex flex-col"
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
    >
      {dragOver ? <DragOverlay isZh={isZh} /> : null}
      <input
        ref={fileInputRef}
        type="file"
        className="hidden"
        multiple
        accept={PERSONAL_DOCUMENT_ACCEPT}
        onChange={handleInputChange}
      />

      <div
        className="mx-6 mt-4 mb-0 flex items-center gap-2 px-3.5 py-2.5 rounded-xl border"
        style={{ backgroundColor: "rgba(131,0,81,0.03)", borderColor: "rgba(131,0,81,0.12)" }}
      >
        <div className="w-5 h-5 flex items-center justify-center shrink-0">
          <i className="ri-lock-line text-sm" style={{ color: "#830051" }}></i>
        </div>
        <p className="text-xs leading-relaxed" style={{ color: "#830051" }}>
          {isZh
            ? "这里的文件仅自己可见，不会向其他用户开放。"
            : "Files here are private to you and are not available to other users. Allowed types: .csv, .xlsx, .xls, .md, .pdf, .txt, .docx, .doc, .pptx, .pptm, .ppt, .msg"}
        </p>
      </div>

      {visibleError ? (
        <div className="mx-6 mt-2 rounded-lg bg-red-50 px-3 py-2 text-xs text-red-600">
          {visibleError}
        </div>
      ) : null}

      <div className="px-6 pt-3 pb-3 flex items-center gap-2 flex-wrap">
        <div
          className="flex items-center gap-1.5 px-3 py-1.5 border border-gray-200 rounded-lg bg-white"
          style={{ minWidth: 220 }}
        >
          <i className="ri-search-line text-gray-400 text-sm shrink-0"></i>
          <input
            type="text"
            value={search}
            onChange={(e) => {
              setPage(1);
              setSearch(e.target.value);
            }}
            placeholder={isZh ? "搜索文件名…" : "Search file name…"}
            className="flex-1 text-sm outline-none bg-transparent text-gray-700 placeholder-gray-400 min-w-0"
          />
          {search ? (
            <button
              onClick={() => {
                setPage(1);
                setSearch("");
              }}
              className="shrink-0 cursor-pointer"
            >
              <i className="ri-close-line text-gray-400 text-sm"></i>
            </button>
          ) : null}
        </div>

        <select
          value={filterKB}
          onChange={(e) => {
            setPage(1);
            setFilterKB(e.target.value);
          }}
          className="text-xs px-3 py-1.5 border border-gray-200 rounded-lg bg-white text-gray-600 outline-none cursor-pointer"
          style={{
            borderColor: filterKB !== "all" ? "#830051" : undefined,
            color: filterKB !== "all" ? "#830051" : undefined,
          }}
        >
          <option value="all">{isZh ? "All KB Status" : "All KB Status"}</option>
          <option value="succeeded">Succeeded</option>
          <option value="running">Running</option>
          <option value="queued">Queued</option>
          <option value="failed">Failed</option>
          <option value="pending">{isZh ? "未开始" : "Not started"}</option>
        </select>

        {hasActiveFilter ? (
          <button
            onClick={resetFilters}
            className="text-xs px-3 py-1.5 rounded-lg border border-gray-200 text-gray-500 cursor-pointer hover:bg-gray-50 transition-colors whitespace-nowrap"
          >
            <i className="ri-refresh-line mr-1"></i>
            {isZh ? "重置" : "Reset"}
          </button>
        ) : null}

        <span
          className={`ml-1 whitespace-nowrap text-xs ${
            maxFileCountReached ? "font-semibold text-red-500" : "text-gray-400"
          }`}
        >
          {fileCount} / {maxFileCount}
          {maxFileCountReached ? (isZh ? " · 已达上限" : " · max reached") : " files"}
        </span>
        <div className="flex-1"></div>

        <Tooltip title={maxFileCountReached ? uploadLimitTooltip : undefined} placement="topRight">
          <span className="inline-flex shrink-0">
            <button
              type="button"
              disabled={maxFileCountReached}
              onClick={() => fileInputRef.current?.click()}
              className={`flex items-center gap-1.5 rounded-lg px-4 py-1.5 text-sm font-medium whitespace-nowrap transition-colors ${
                maxFileCountReached
                  ? "cursor-not-allowed bg-gray-200 text-gray-400"
                  : "cursor-pointer bg-[#830051] text-white hover:opacity-90"
              }`}
            >
              <i className="ri-upload-cloud-line text-base"></i>
              {isZh ? "上传" : "Upload"}
            </button>
          </span>
        </Tooltip>
      </div>

      <div className="mx-6 mb-4 rounded-xl border border-gray-100 bg-white overflow-visible">
        <div className="flex items-center gap-5 px-6 py-3 bg-gray-50 border-b border-gray-100">
          <div className="w-8 shrink-0 flex items-center justify-center">
            <Checkbox
              checked={allDisplayedSelected}
              indeterminate={someDisplayedSelected}
              onChange={toggleSelectAll}
            />
          </div>
          <div className="w-8 shrink-0"></div>
          <div className="flex-1 min-w-0 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">
            {isZh ? "文件名" : "File Name"}
          </div>
          <button
            onClick={() => toggleSort("uploadedAt")}
            className="w-32 shrink-0 flex items-center gap-1 text-xs font-semibold text-gray-500 uppercase tracking-wide cursor-pointer hover:text-gray-700 transition-colors"
          >
            {isZh ? "上传时间" : "Uploaded"} <SortIcon k="uploadedAt" />
          </button>
          <div className="w-32 shrink-0 text-xs font-semibold text-gray-500 uppercase tracking-wide text-right">
            KB Status
          </div>
        </div>

        <div>
          {displayed.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16 gap-3">
              <div className="w-12 h-12 rounded-full bg-gray-100 flex items-center justify-center">
                <i className="ri-folder-open-line text-xl text-gray-400"></i>
              </div>
              <p className="text-sm font-medium text-gray-500">
                {isZh ? "没有匹配的文件" : "No files found"}
              </p>
              <p className="text-xs text-gray-400">
                {hasActiveFilter
                  ? isZh
                    ? "尝试调整筛选条件"
                    : "Try adjusting filters"
                  : isZh
                    ? "拖拽文件到此处，或点击「上传」开始添加"
                    : "Drag & drop files here, or click Upload"}
              </p>
            </div>
          ) : (
            displayed.map((doc, index) => {
              const isSelected = selectedIds.has(doc.id);
              const isLast = index === displayed.length - 1;
              const kbStatus = kbMap[doc.id];
              const uploadItem = uploadQueue.items.find((item) => item.documentListId === doc.id);
              const canCancelUpload =
                uploadItem?.phase === "waiting" || uploadItem?.phase === "uploading";
              const isRetrying = retryingIds.has(doc.id);
              const retryLabel = isZh
                ? isRetrying
                  ? "重试中"
                  : uploadItem
                    ? "重试上传"
                    : "重试构建"
                : isRetrying
                  ? "Retrying"
                  : uploadItem
                    ? "Retry upload"
                    : "Retry build";

              return (
                <div
                  key={doc.id}
                  className={`flex items-center gap-5 px-6 py-4 transition-colors ${
                    !isLast ? "border-b border-gray-100" : ""
                  } ${isSelected ? "bg-[rgba(131,0,81,0.03)]" : "hover:bg-gray-50/50"}`}
                >
                  <div className="w-8 shrink-0 flex items-center justify-center">
                    <Checkbox checked={isSelected} onChange={() => toggleSelectRow(doc.id)} />
                  </div>
                  <div
                    className="w-8 h-8 flex items-center justify-center rounded-lg shrink-0"
                    style={{ backgroundColor: `${TYPE_COLOR[doc.type] ?? "#6b7280"}12` }}
                  >
                    <i
                      className={`${TYPE_ICON[doc.type] ?? "ri-file-line"} text-sm`}
                      style={{ color: TYPE_COLOR[doc.type] ?? "#6b7280" }}
                    ></i>
                  </div>

                  <div className="flex-1 min-w-0">
                    <button
                      type="button"
                      onClick={() => void downloadDocument(doc.id)}
                      className="block max-w-full text-left text-sm font-medium text-gray-800 truncate hover:text-[#830051]"
                      title={doc.name}
                    >
                      {doc.name}
                    </button>
                    <div className="flex items-center flex-wrap gap-1 mt-0.5">
                      <span className="text-[11px] text-gray-400">{doc.size}</span>
                    </div>
                  </div>

                  <div className="w-32 shrink-0">
                    <span className="text-xs text-gray-500 whitespace-nowrap">
                      {doc.uploadedAt}
                    </span>
                  </div>

                  <div className="w-32 shrink-0 flex flex-col items-end gap-1">
                    <div className="flex items-center justify-end gap-1">
                      <KBBadge status={kbStatus} isZh={isZh} />
                      {kbStatus?.status === "failed" ? (
                        <Tooltip title={retryLabel} placement="top">
                          <button
                            type="button"
                            aria-label={retryLabel}
                            aria-busy={isRetrying}
                            disabled={isRetrying}
                            onClick={() => void retryFailedDocument(doc.id)}
                            className="inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-[#830051] transition-colors hover:bg-[rgba(131,0,81,0.08)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#830051]/30 disabled:cursor-not-allowed disabled:opacity-60"
                          >
                            <i
                              className={`${isRetrying ? "ri-loader-4-line animate-spin" : "ri-refresh-line"} text-sm`}
                              aria-hidden="true"
                            ></i>
                          </button>
                        </Tooltip>
                      ) : null}
                    </div>
                    {canCancelUpload ? (
                      <button
                        type="button"
                        onClick={() => uploadQueue.cancel(uploadItem.clientId)}
                        className="text-[11px] text-gray-500 hover:text-red-500"
                      >
                        {isZh ? "取消上传" : "Cancel"}
                      </button>
                    ) : null}
                  </div>
                </div>
              );
            })
          )}
        </div>

        {filteredTotal > 0 ? (
          <div className="flex items-center justify-between border-t border-gray-100 px-5 py-3">
            <span className="text-xs text-gray-400">
              {isZh ? `共 ${filteredTotal} 项` : `Total ${filteredTotal} items`}
            </span>
            <Pagination
              current={page}
              pageSize={PAGE_SIZE}
              showSizeChanger={false}
              size="small"
              total={filteredTotal}
              onChange={setPage}
            />
          </div>
        ) : null}
      </div>

      {selectedCount > 0 ? (
        <div
          className="fixed bottom-6 left-1/2 -translate-x-1/2 z-40 flex items-center gap-3 px-5 py-3 bg-white border border-gray-200 rounded-2xl"
          style={{ boxShadow: "0 4px 20px rgba(0,0,0,0.08)" }}
        >
          <span className="text-xs font-medium text-gray-700">
            {isZh
              ? `已选择 ${selectedCount} 个文件`
              : `${selectedCount} file${selectedCount > 1 ? "s" : ""} selected`}
          </span>
          <div className="w-px h-4 bg-gray-200"></div>
          <button
            onClick={() => setShowDeleteDialog(true)}
            className="flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-lg text-white cursor-pointer whitespace-nowrap transition-opacity hover:opacity-90"
            style={{ backgroundColor: "#ef4444" }}
          >
            <i className="ri-delete-bin-line"></i>
            {isZh ? "删除" : "Delete"}
          </button>
          <button
            onClick={() => setSelectedIds(new Set())}
            className="text-xs px-3 py-1.5 rounded-lg border border-gray-200 text-gray-500 cursor-pointer whitespace-nowrap hover:bg-gray-50 transition-colors"
          >
            {isZh ? "取消选择" : "Deselect"}
          </button>
        </div>
      ) : null}

      {showDeleteDialog ? (
        <DeleteConfirmDialog
          isZh={isZh}
          count={selectedCount}
          onCancel={() => setShowDeleteDialog(false)}
          onConfirm={handleDelete}
        />
      ) : null}
    </div>
  );
}
